import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './services/AuthContext';
import { ErrorProvider, useError } from './services/ErrorContext';
import { setErrorHandler } from './services/api';
import Login from './pages/Login';
import Landing from './pages/LandingPage';
import Register from './pages/Register';
import DashboardLayout from './componets/DashboardLayout';
import TopicIntelligence from './pages/Test';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contact';
import AddContact from './pages/AddContact';
import GenerateMessage from './pages/GenerateMessage';
import ContactDetail from './pages/ContactDetails';
import Scheduler from './pages/Schedular';
import UserProfile from './pages/UserProfile';
import TopicScheduler from './pages/TopicScheduler';
import TopicBriefings from './pages/TopicBreifing';
import './App.css';
import AdminDashboard from './admin/AdminDashboard';
import StatusPage from './pages/PublicStatusPage';
import BugReportButton from './componets/ReportBug';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FloatingAIAssistant from './componets/FloatingAIAssistant';
import { contactService } from './services/api';
import NetworkingCalendar from './pages/NetworkCalender';
import AITranscriber from './pages/AITranscriber';
import PaymentSuccess from './subscription/PaymentSuccess'; 
import OneTimePaymentSuccess from './subscription/OneTimePaymentSuccess';
import Tasks from './pages/Tasks';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
      }}>
        <div style={{ fontSize: '1.2rem', color: '#475569' }}>Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
      }}>
        <div style={{ fontSize: '1.2rem', color: 'white' }}>Loading...</div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : children;
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const { showError } = useError();

  useEffect(() => {
    if (user) {
      loadContacts();
    }
  }, [user]);

  useEffect(() => {
    setErrorHandler(showError);
  }, [showError]);

  const loadContacts = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const shouldShowAIAssistant = user && location.pathname !== '/';

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/test" element={<TopicIntelligence />} />
        <Route path="/PublicStatus" element={<StatusPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/onetime-payment-success" element={<OneTimePaymentSuccess />} />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="add-contact" element={<AddContact />} />
          <Route path="messages" element={<GenerateMessage />} />
          <Route path="schedule" element={<Scheduler />} />
          <Route path="topic-scheduler" element={<TopicIntelligence />} />
          <Route path="briefings" element={<TopicBriefings />} />
          <Route path="settings" element={<UserProfile />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="news" element={<TopicIntelligence />} />
          <Route path="calendar" element={<NetworkingCalendar />} />
          <Route path="transcribe" element={<AITranscriber />} />
          <Route path="tasks" element={<Tasks />} />

        </Route>
      </Routes>
      <BugReportButton />
      {shouldShowAIAssistant && <FloatingAIAssistant contacts={contacts} />}
    </>
  );
}

function App() {
  return (
    <ErrorProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ErrorProvider>
  );
}

export default App;